import React from 'react';
import {
    Button,
    Dropdown,
    Field,
    Input,
    Option,
    Spinner,
    Switch,
    Text,
    Textarea,
    Tooltip,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';

import { Controller, useFieldArray, useForm } from 'react-hook-form';
import AgentFieldLabel from '../shared/AgentFieldLabel';
import { AgentFile } from '../../../../domain/agents/models/Agent';
import { getAcceptedAgentUploadExtensions, validateAndSanitizeAgentUploadFiles } from '../../../utils/FileUtils';
import { AlwaysOnScrollBarStyles, customColors, customFonts } from '../../../../styles';
import {
    ArrowLeft24Regular,
    Info16Regular,
    Info24Regular,
    ChevronDownRegular,
    ChevronRightRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import useEditAgentPage from '../hooks/useEditAgentPage';
import {
    AGENT_UPLOAD_MAX_FILE_COUNT,
    AGENT_UPLOAD_MAX_FILE_SIZE_MB,
    EditAgentFormValues,
    toolOptions,
    companyEmailPattern,
    DESCRIPTION_MAX_LENGTH,
    INSTRUCTIONS_MAX_LENGTH,
    CARD_SHORT_DESCRIPTION_MAX_LENGTH,
    DISCLAIMER_MAX_LENGTH,
    KNOWLEDGE_BASE_MAX_LENGTH,
    SHORT_ENTRY_MAX_LENGTH,
    PROMPT_DESCRIPTION_MAX_LENGTH,
    PROMPT_MAX_ITEMS,
    FAQ_MAX_ITEMS,
    CAPABILITY_MAX_LENGTH,
    CAPABILITIES_MAX_ITEMS,
    RELATED_LINK_MAX_ITEMS,
} from '../shared/AgentFormShared';
import { mapFunctionToolsFormToRequest } from '../shared/FunctionToolForm';
import FunctionToolsFieldArray from '../shared/FunctionToolsFieldArray';

const parseModels = (): string[] => {
    try {
        const raw = JSON.parse(process.env.REACT_APP_MODELS || '[]');
        return Array.isArray(raw) ? raw.filter((model) => typeof model === 'string' && model.length > 0) : [];
    } catch {
        return [];
    }
};

const useStyles = makeStyles({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        ...shorthands.overflow('auto'),
        backgroundColor: customColors.protivitiOffWhite,
        ...AlwaysOnScrollBarStyles,
    },
    content: {
        width: '70%',
        maxWidth: '980px',
        ...shorthands.padding('40px', '20px', '40px', '20px'),
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        boxSizing: 'border-box',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('12px'),
        ...shorthands.padding('16px'),
        ...shorthands.borderRadius('12px'),
        backgroundColor: customColors.white,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
        border: `1px solid ${customColors.lightGray}`,
    },
    title: {
        marginBottom: '20px',
        fontSize: '3em',
        fontWeight: '500',
        display: 'flex',
        justifyContent: 'center',
        color: customColors.summarizeButtonDarker,
        fontFamily: customFonts.TitleFont,
        lineHeight: '80%',
    },
    subtitle: {
        fontSize: '2em',
        marginBottom: '10px',
        marginTop: '-10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: '300',
        color: customColors.headerLightGray,
        fontFamily: customFonts.TitleFont,
    },
    fieldLabel: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        verticalAlign: 'middle',
    },
    fieldName: {
        marginBottom: '5px',
        fontSize: '1.5em',
        fontWeight: '500',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        color: customColors.summarizeButtonDarker,
        fontFamily: customFonts.TitleFont,
    },
    tooltipIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: customColors.summarizeButtonDarker,
        cursor: 'pointer',
        transform: 'scale(0.8)',
        verticalAlign: 'middle',
        marginTop: '1px',
    },
    tooltipContent: {
        maxWidth: '360px',
        whiteSpace: 'normal',
        fontFamily: customFonts.Barlow,
        fontSize: '13px',
        lineHeight: 1.4,
    },
    characterCount: {
        fontFamily: customFonts.Barlow,
        color: customColors.headerLightGray,
        fontSize: '12px',
        textAlign: 'right',
    },
    uploadImageRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    textHint: {
        fontFamily: customFonts.Barlow,
        color: customColors.headerLightGray,
        fontSize: '14px',
    },
    mcpOptionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    mcpOptionInfoIcon: {
        color: tokens.colorNeutralForeground3,
        flexShrink: 0,
    },
    uploadRow: {
        display: 'flex',
        ...shorthands.gap('10px'),
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    uploadRowRight: {
        display: 'flex',
        ...shorthands.gap('10px'),
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
    },
    fileTable: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: customColors.white,
    },
    fileTableHeader: {
        display: 'grid',
        gridTemplateColumns: '48px 1fr 180px',
        alignItems: 'center',
        backgroundColor: tokens.colorNeutralBackground1,
        ...shorthands.padding('10px', '12px'),
        ...shorthands.borderBottom('1px', 'solid', customColors.lightGray),
        columnGap: '12px',
    },
    fileTableRow: {
        display: 'grid',
        gridTemplateColumns: '48px 1fr 180px',
        alignItems: 'center',
        ...shorthands.padding('10px', '12px'),
        columnGap: '12px',
        ...shorthands.borderBottom('1px', 'solid', customColors.lightGray),
        ':last-child': {
            borderBottom: 'none',
        },
    },
    fileTableHeaderText: {
        fontFamily: customFonts.TitleFont,
        color: customColors.summarizeButtonDarker,
        fontSize: '16px',
        fontWeight: '500',
    },
    fileNameText: {
        fontSize: '14px',
        fontWeight: '400',
    },
    fileDateText: {
        fontSize: '14px',
        fontWeight: '400',
    },
    inlineGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: 'fit-content',
    },
    buttonRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        ...shorthands.gap('10px'),
        ...shorthands.margin('8px', '0', '0', '0'),
        ...shorthands.padding('0', '0', '40px', '0'),
    },
    errorText: {
        color: '#b42318',
        fontFamily: customFonts.Barlow,
        fontSize: '13px',
    },
    select: {
        width: '100%',
        minHeight: '36px',
        ...shorthands.border('2px', 'solid', customColors.headerLightGray),
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('6px', '8px'),
        backgroundColor: customColors.white,
        fontFamily: customFonts.Barlow,
    },
    removeButton: {
        alignSelf: 'flex-start',
    },
    compactButton: {
        width: 'fit-content',
        ...shorthands.borderRadius('5px'),
    },
    subItemCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        ...shorthands.padding('14px'),
        ...shorthands.borderRadius('10px'),
        backgroundColor: '#fafafa',
    },
    inlineActionRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '10px',
    },
    addButtonRow: {
        ...shorthands.padding('0', '0', '0', '14px'),
    },
    hiddenFileInput: {
        display: 'none',
    },
    iconPreview: {
        width: '48px',
        height: '48px',
        borderRadius: '8px',
        objectFit: 'cover',
    },
    actionButton: {
        ...shorthands.borderRadius('5px'),
    },
    collapsibleHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        ...shorthands.padding(0),
        ...shorthands.border('0'),
        textAlign: 'left',
    },
    collapsibleHeaderLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    collapseIcon: {
        color: customColors.summarizeButtonDarker,
    },
    metadataSeparator: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        ...shorthands.margin('32px', '0'),
    },
    metadataSeparatorText: {
        color: customColors.summarizeButtonDarker,
        fontFamily: customFonts.TitleFont,
        fontSize: '2em',
        fontWeight: '500',
        lineHeight: 1,
    },
    metadataSeparatorLine: {
        flexGrow: 1,
        height: '1px',
        backgroundColor: customColors.lightGray,
    },
    backButtonRow: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-start',
        ...shorthands.margin('0px', '0px', '8px', '0px'),
    },
    backButton: {
        height: '28px',
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        fontWeight: '700',
        textDecoration: 'underline',
        color: customColors.protivitiDarkBlue,
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '10px'),
        '&:hover': {
            color: customColors.summarizeButtonDarker,
        },
    },
});

const formatUploadedAt = (uploadedAt?: string): string => {
    if (!uploadedAt) {
        return 'Pending upload';
    }

    const date = new Date(uploadedAt);
    if (Number.isNaN(date.getTime())) {
        return uploadedAt;
    }

    return date.toLocaleString();
};

// TODO: wire up image uploads and agent owner fields
const EditAgentPage: React.FC = () => {
    const styles = useStyles();
    const vm = useEditAgentPage();
    const navigate = useNavigate();

    const fileSearchInputRef = React.useRef<HTMLInputElement | null>(null);
    const codeInterpreterInputRef = React.useRef<HTMLInputElement | null>(null);
    const [isFileSearchPendingFilesExpanded, setIsFileSearchPendingFilesExpanded] = React.useState(true);
    const [isCodeInterpreterPendingFilesExpanded, setIsCodeInterpreterPendingFilesExpanded] = React.useState(true);
    const [isFileSearchFilesExpanded, setIsFileSearchFilesExpanded] = React.useState(true);
    const [isCodeInterpreterFilesExpanded, setIsCodeInterpreterFilesExpanded] = React.useState(true);
    const [fileSearchFilesToUpload, setFileSearchFilesToUpload] = React.useState<File[]>([]);
    const [codeInterpreterFilesToUpload, setCodeInterpreterFilesToUpload] = React.useState<File[]>([]);
    const [uploadedFileSearchDocuments, setUploadedFileSearchDocuments] = React.useState<AgentFile[]>([]);
    const [fileSearchDocumentsToDelete, setFileSearchDocumentsToDelete] = React.useState<Set<string>>(new Set());
    const [uploadedCodeInterpreterDocuments, setUploadedCodeInterpreterDocuments] = React.useState<AgentFile[]>([]);
    const [codeInterpreterDocumentsToDelete, setCodeInterpreterDocumentsToDelete] = React.useState<Set<string>>(
        new Set(),
    );
    const [selectedFileSearchFilesToUpload, setSelectedFileSearchFilesToUpload] = React.useState<Set<string>>(
        new Set(),
    );
    const [selectedCodeInterpreterFilesToUpload, setSelectedCodeInterpreterFilesToUpload] = React.useState<Set<string>>(
        new Set(),
    );
    const [selectedUploadedFileSearchDocuments, setSelectedUploadedFileSearchDocuments] = React.useState<Set<string>>(
        new Set(),
    );
    const [selectedUploadedCodeInterpreterDocuments, setSelectedUploadedCodeInterpreterDocuments] = React.useState<
        Set<string>
    >(new Set());
    const imageInputRef = React.useRef<HTMLInputElement | null>(null);
    const [selectedImageFile, setSelectedImageFile] = React.useState<File | null>(null);

    const models = React.useMemo(() => parseModels(), []);

    const maxFileCount = AGENT_UPLOAD_MAX_FILE_COUNT;
    const maxFileSizeMb = AGENT_UPLOAD_MAX_FILE_SIZE_MB;
    const availableMcpServers = vm.availableMcpServers;
    const isMcpServersLoading = vm.isMcpServersLoading;

    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<EditAgentFormValues>({
        defaultValues: {
            description: '',
            model: models[0] ?? 'gpt-5.4-mini',
            instructions: '',
            tools: [],
            functionTools: [],
            allowFileSearchAtConversationLevel: false,
            allowCodeInterpreterAtConversationLevel: false,
            selectedMcpServerIds: [],
            agentIconUrl: '',
            cardShortDescription: '',
            disclaimer: '',
            knowledgeBaseContent: '',
            promptLibrary: [{ title: '', prompt: '' }],

            faqs: [{ question: '', answer: '' }],
            ownerInfo: [
                {
                    userId: '',
                    givenName: '',
                    surName: '',
                    userPrincipalName: '',
                    mail: '',
                    photoUrl: '',
                },
            ],
            supportAddress: [{ email: '' }],
            capabilities: [{ value: '' }],
            relatedLinks: [{ title: '', link: '' }],
        },
    });

    const {
        fields: promptFields,
        append: appendPrompt,
        remove: removePrompt,
    } = useFieldArray({ control, name: 'promptLibrary' });
    const { fields: faqFields, append: appendFaq, remove: removeFaq } = useFieldArray({ control, name: 'faqs' });
    const {
        fields: ownerFields,
        append: appendOwner,
        remove: removeOwner,
    } = useFieldArray({ control, name: 'ownerInfo' });
    const {
        fields: supportAddressFields,
        append: appendSupportAddress,
        remove: removeSupportAddress,
    } = useFieldArray({ control, name: 'supportAddress' });
    const {
        fields: capabilityFields,
        append: appendCapability,
        remove: removeCapability,
    } = useFieldArray({ control, name: 'capabilities' });

    const {
        fields: relatedLinkFields,
        append: appendRelatedLink,
        remove: removeRelatedLink,
    } = useFieldArray({ control, name: 'relatedLinks' });

    const selectedTools = watch('tools');
    const enableFileSearch = selectedTools.includes('fileSearch');
    const enableCodeInterpreter = selectedTools.includes('codeInterpreter');
    const enableFunctionCalling = selectedTools.includes('functionCalling');
    const enableMcpServer = selectedTools.includes('mcpServer');
    const fileSearchAcceptedExtensions = React.useMemo(() => getAcceptedAgentUploadExtensions(false, true), []);
    const codeInterpreterAcceptedExtensions = React.useMemo(() => getAcceptedAgentUploadExtensions(true, false), []);

    const selectedToolLabels = React.useMemo(
        () =>
            toolOptions
                .filter((tool) => selectedTools.includes(tool.value))
                .map((tool) => tool.label)
                .join(', '),
        [selectedTools],
    );

    const validateCompanyEmail = (value?: string): true | string => {
        if (!value?.trim()) {
            return true;
        }

        return companyEmailPattern.test(value.trim()) ? true : 'Must be a roberthalf.com or protiviti.com address.';
    };

    const handleToolChange = (values: string[]): void => {
        setValue('tools', values, { shouldValidate: true, shouldDirty: true });
    };

    const handleOpenFilePicker = (inputRef: React.RefObject<HTMLInputElement>): void => {
        inputRef.current?.click();
    };

    const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setSelectedImageFile(file);
        setValue('agentIconUrl', file.name, { shouldDirty: true });
        event.target.value = '';
    };

    const handleUploadFiles = async (
        event: React.ChangeEvent<HTMLInputElement>,
        options: {
            currentFiles: File[];
            setCurrentFiles: React.Dispatch<React.SetStateAction<File[]>>;
            acceptedExtensions: string[];
        },
    ): Promise<void> => {
        const inputFiles = event.target.files;
        if (!inputFiles || inputFiles.length === 0) {
            return;
        }

        const totalSelectedCount =
            uploadedFileSearchDocuments.length +
            uploadedCodeInterpreterDocuments.length +
            fileSearchFilesToUpload.length +
            codeInterpreterFilesToUpload.length;

        const result = await validateAndSanitizeAgentUploadFiles(Array.from(inputFiles), {
            currentCount: totalSelectedCount,
            maxFileCount,
            maxFileSizeMb,
            acceptedExtensions: options.acceptedExtensions,
        });

        const existing = new Set(options.currentFiles.map((file) => `${file.name}-${file.size}`));
        const deduped = result.acceptedFiles.filter((file) => !existing.has(`${file.name}-${file.size}`));
        options.setCurrentFiles((previous) => [...previous, ...deduped]);
        event.target.value = '';
    };

    const toggleSelection = (key: string, setter: React.Dispatch<React.SetStateAction<Set<string>>>): void => {
        setter((previous) => {
            const next = new Set(previous);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleDeleteSelectedFilesToUpload = (
        selectedFilesToUpload: Set<string>,
        setFilesToUpload: React.Dispatch<React.SetStateAction<File[]>>,
        setSelectedFilesToUpload: React.Dispatch<React.SetStateAction<Set<string>>>,
    ): void => {
        if (selectedFilesToUpload.size === 0) {
            return;
        }

        setFilesToUpload((previous) =>
            previous.filter((file) => !selectedFilesToUpload.has(`${file.name}-${file.size}`)),
        );
        setSelectedFilesToUpload(new Set());
    };

    const handleDeleteSelectedUploadedFileSearchDocuments = (): void => {
        if (selectedUploadedFileSearchDocuments.size === 0) {
            return;
        }

        setFileSearchDocumentsToDelete((previous) => {
            const next = new Set(previous);
            selectedUploadedFileSearchDocuments.forEach((fileName) => next.add(fileName));
            return next;
        });
        setSelectedUploadedFileSearchDocuments(new Set());
    };

    const handleDeleteSelectedUploadedCodeInterpreterDocuments = (): void => {
        if (selectedUploadedCodeInterpreterDocuments.size === 0) {
            return;
        }

        setCodeInterpreterDocumentsToDelete((previous) => {
            const next = new Set(previous);
            selectedUploadedCodeInterpreterDocuments.forEach((fileName) => next.add(fileName));
            return next;
        });
        setSelectedUploadedCodeInterpreterDocuments(new Set());
    };

    const handleDeleteAllFiles = (): void => {
        setFileSearchFilesToUpload([]);
        setCodeInterpreterFilesToUpload([]);
        setSelectedFileSearchFilesToUpload(new Set());
        setSelectedCodeInterpreterFilesToUpload(new Set());
        setFileSearchDocumentsToDelete(new Set(uploadedFileSearchDocuments.map((file) => file.fileName)));
        setCodeInterpreterDocumentsToDelete(new Set(uploadedCodeInterpreterDocuments.map((file) => file.fileName)));
        setSelectedUploadedFileSearchDocuments(new Set());
        setSelectedUploadedCodeInterpreterDocuments(new Set());
    };

    const handleToggleSelectAll = (
        items: string[],
        selectedItems: Set<string>,
        setter: React.Dispatch<React.SetStateAction<Set<string>>>,
    ): void => {
        if (items.length === 0) {
            return;
        }

        if (selectedItems.size === items.length) {
            setter(new Set());
            return;
        }

        setter(new Set(items));
    };

    React.useEffect(() => {
        setUploadedFileSearchDocuments(vm.fileSearchDocuments);
    }, [vm.fileSearchDocuments]);

    React.useEffect(() => {
        setUploadedCodeInterpreterDocuments(vm.codeInterpreterDocuments);
    }, [vm.codeInterpreterDocuments]);

    React.useEffect(() => {
        if (enableFileSearch) {
            setFileSearchDocumentsToDelete(new Set());
            return;
        }

        setFileSearchDocumentsToDelete(new Set(uploadedFileSearchDocuments.map((file) => file.fileName)));
    }, [enableFileSearch, uploadedFileSearchDocuments]);

    React.useEffect(() => {
        if (enableCodeInterpreter) {
            setCodeInterpreterDocumentsToDelete(new Set());
            return;
        }

        setCodeInterpreterDocumentsToDelete(new Set(uploadedCodeInterpreterDocuments.map((file) => file.fileName)));
    }, [enableCodeInterpreter, uploadedCodeInterpreterDocuments]);

    React.useEffect(() => {
        if (!vm.formStarterValues) {
            return;
        }

        reset({
            description: vm.formStarterValues.description,
            model: vm.formStarterValues.model || models[0] || 'gpt-5.4-mini',
            instructions: vm.formStarterValues.instructions,
            tools: vm.formStarterValues.tools,
            functionTools: vm.formStarterValues.functionTools,
            agentIconUrl: vm.formStarterValues.agentIconUrl,
            cardShortDescription: vm.formStarterValues.cardShortDescription,
            disclaimer: vm.formStarterValues.disclaimer,
            knowledgeBaseContent: vm.formStarterValues.knowledgeBaseContent,
            promptLibrary:
                vm.formStarterValues.promptLibrary.length > 0
                    ? vm.formStarterValues.promptLibrary
                    : [{ title: '', prompt: '' }],
            faqs: vm.formStarterValues.faqs,
            ownerInfo: vm.formStarterValues.ownerInfo,
            supportAddress: vm.formStarterValues.supportAddress
                ? [{ email: vm.formStarterValues.supportAddress }]
                : [{ email: '' }],
            capabilities:
                vm.formStarterValues.capabilities.length > 0
                    ? vm.formStarterValues.capabilities.map((v) => ({ value: v }))
                    : [{ value: '' }],
            relatedLinks: vm.formStarterValues.relatedLinks,
            allowFileSearchAtConversationLevel: vm.formStarterValues.allowFileSearchAtConversationLevel,
            allowCodeInterpreterAtConversationLevel: vm.formStarterValues.allowCodeInterpreterAtConversationLevel,
            selectedMcpServerIds: vm.formStarterValues.selectedMcpServerIds,
        });
    }, [models, reset, vm.formStarterValues]);

    const visibleUploadedFileSearchDocuments = uploadedFileSearchDocuments.filter(
        (file) => !fileSearchDocumentsToDelete.has(file.fileName),
    );
    const visibleUploadedCodeInterpreterDocuments = uploadedCodeInterpreterDocuments.filter(
        (file) => !codeInterpreterDocumentsToDelete.has(file.fileName),
    );
    const totalUploadedFilesCount =
        visibleUploadedFileSearchDocuments.length +
        visibleUploadedCodeInterpreterDocuments.length +
        fileSearchFilesToUpload.length +
        codeInterpreterFilesToUpload.length;

    const descriptionValue = watch('description') ?? '';
    const instructionsValue = watch('instructions') ?? '';
    const cardShortDescriptionValue = watch('cardShortDescription') ?? '';
    const disclaimerValue = watch('disclaimer') ?? '';
    const knowledgeBaseValue = watch('knowledgeBaseContent') ?? '';

    const onSubmit = async (values: EditAgentFormValues): Promise<void> => {
        const metadata: Record<string, unknown> = {};

        if (values.cardShortDescription.trim()) {
            metadata.cardShortDescription = values.cardShortDescription.trim();
        }

        if (values.disclaimer.trim()) {
            metadata.disclaimer = values.disclaimer.trim();
        }

        if (values.knowledgeBaseContent.trim()) {
            metadata.agentKnowledgeBase = { content: values.knowledgeBaseContent.trim() };
        }

        const validPrompts = values.promptLibrary
            .map((prompt) => ({
                title: prompt.title?.trim() || undefined,
                prompt: prompt.prompt.trim(),
            }))
            .filter((prompt) => (prompt.title?.length ?? 0) > 0 || prompt.prompt.length > 0);
        if (validPrompts.length > 0) {
            metadata.promptLibrary = [
                {
                    category: 'Conversation Starters',
                    items: validPrompts,
                },
            ];
        }

        const validFaqs = values.faqs.filter((f) => f.question.trim() || f.answer.trim());
        if (validFaqs.length > 0) {
            metadata.faqs = validFaqs;
        }

        const validOwners = values.ownerInfo
            .map((owner) => owner.userPrincipalName?.trim())
            .filter((userPrincipalName): userPrincipalName is string => Boolean(userPrincipalName));
        if (validOwners.length > 0) {
            metadata.agentOwnerInfo = validOwners.map((userPrincipalName) => ({
                userId: '',
                givenName: '',
                surName: '',
                userPrincipalName,
                mail: '',
                photoUrl: '',
            }));
        }

        const supportEmail = values.supportAddress?.[0]?.email?.trim();
        if (supportEmail) {
            metadata.supportAddress = supportEmail;
        }

        const validCapabilities = values.capabilities.map((c) => c.value.trim()).filter(Boolean);
        if (validCapabilities.length > 0) {
            metadata.capabilities = validCapabilities;
        }

        const validLinks = values.relatedLinks.filter((l) => l.title.trim() || l.link.trim());
        if (validLinks.length > 0) {
            metadata.relatedLinks = validLinks;
        }

        metadata.allowFileSearchAtConversationLevel = values.allowFileSearchAtConversationLevel;
        metadata.allowCodeInterpreterAtConversationLevel = values.allowCodeInterpreterAtConversationLevel;

        const functionTools = enableFunctionCalling ? mapFunctionToolsFormToRequest(values.functionTools) : [];

        await vm.onSubmit({
            instructions: values.instructions.trim() || undefined,
            description: values.description.trim() || undefined,
            agentIconFile: selectedImageFile ?? undefined,
            model: values.model.trim() || undefined,
            filesForFileSearch: enableFileSearch ? fileSearchFilesToUpload : undefined,
            filesForCodeInterpreter: enableCodeInterpreter ? codeInterpreterFilesToUpload : undefined,
            enableCodeInterpreter,
            enableFileSearch,
            functionTools,
            mcpServerIds: values.selectedMcpServerIds.length > 0 ? values.selectedMcpServerIds : undefined,
            filesToDeleteForFileSearch: Array.from(fileSearchDocumentsToDelete),
            filesToDeleteForCodeInterpreter: Array.from(codeInterpreterDocumentsToDelete),
            metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        });
    };

    return (
        <div className={styles.root}>
            <form className={styles.content} onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.backButtonRow}>
                    <Button
                        type="button"
                        appearance="transparent"
                        className={styles.backButton}
                        icon={<ArrowLeft24Regular />}
                        onClick={() => navigate('/manage-agents')}
                    >
                        Back to Manage Agents
                    </Button>
                </div>
                <Text className={styles.title}>{vm.agentName ? `Edit Agent: ${vm.agentName}` : 'Edit Agent'}</Text>

                {vm.isLoadingAgent ? (
                    <Text>Loading agent...</Text>
                ) : vm.isAgentMissing ? (
                    <Text>Agent not found.</Text>
                ) : (
                    <>
                        <Field
                            label={
                                <AgentFieldLabel
                                    label="Description"
                                    tooltip="Provide a brief description of what this agent does or its purpose."
                                    fieldLabelClassName={styles.fieldLabel}
                                    fieldNameClassName={styles.fieldName}
                                    tooltipIconClassName={styles.tooltipIcon}
                                    tooltipContentClassName={styles.tooltipContent}
                                />
                            }
                            validationMessage={errors.description?.message}
                            validationState={errors.description ? 'error' : 'none'}
                        >
                            <Controller
                                name="description"
                                control={control}
                                rules={{
                                    maxLength: {
                                        value: DESCRIPTION_MAX_LENGTH,
                                        message: `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters.`,
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        value={field.value ?? ''}
                                        onChange={(event) => field.onChange(event.target.value)}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        maxLength={DESCRIPTION_MAX_LENGTH}
                                        placeholder="What this agent does"
                                    />
                                )}
                            />
                            <Text className={styles.characterCount}>
                                {descriptionValue.length}/{DESCRIPTION_MAX_LENGTH} characters
                            </Text>
                        </Field>

                        <Field
                            label={
                                <AgentFieldLabel
                                    label="Model"
                                    tooltip="Select the model that powers your agent. The model determines the capabilities and performance of the agent."
                                    fieldLabelClassName={styles.fieldLabel}
                                    fieldNameClassName={styles.fieldName}
                                    tooltipIconClassName={styles.tooltipIcon}
                                    tooltipContentClassName={styles.tooltipContent}
                                />
                            }
                            required={true}
                        >
                            <select
                                className={styles.select}
                                {...register('model', { required: 'Model is required.' })}
                            >
                                {models.length > 0 ? (
                                    models.map((model) => (
                                        <option key={model} value={model}>
                                            {model}
                                        </option>
                                    ))
                                ) : (
                                    <option value="gpt-5.4-mini">gpt-5.4-mini</option>
                                )}
                            </select>
                        </Field>

                        <Field
                            label={
                                <AgentFieldLabel
                                    label="Instructions"
                                    tooltip="Specify the persona details that define how this agent behaves or interacts. Personas influence the tone and style of responses."
                                    fieldLabelClassName={styles.fieldLabel}
                                    fieldNameClassName={styles.fieldName}
                                    tooltipIconClassName={styles.tooltipIcon}
                                    tooltipContentClassName={styles.tooltipContent}
                                />
                            }
                            required={true}
                            validationMessage={errors.instructions?.message}
                            validationState={errors.instructions ? 'error' : 'none'}
                        >
                            <Textarea
                                {...register('instructions', {
                                    required: 'Instructions are required.',
                                    maxLength: {
                                        value: INSTRUCTIONS_MAX_LENGTH,
                                        message: `Instructions cannot exceed ${INSTRUCTIONS_MAX_LENGTH} characters.`,
                                    },
                                })}
                                maxLength={INSTRUCTIONS_MAX_LENGTH}
                                placeholder="Agent persona"
                                style={{ minHeight: '220px' }}
                            />
                            <Text className={styles.characterCount}>
                                {instructionsValue.length}/{INSTRUCTIONS_MAX_LENGTH} characters
                            </Text>
                        </Field>

                        <Field
                            label={
                                <AgentFieldLabel
                                    label="Agent Tools"
                                    tooltip="Select the tools this agent can use, such as File Search or Code Interpreter."
                                    fieldLabelClassName={styles.fieldLabel}
                                    fieldNameClassName={styles.fieldName}
                                    tooltipIconClassName={styles.tooltipIcon}
                                    tooltipContentClassName={styles.tooltipContent}
                                />
                            }
                        >
                            <Dropdown
                                multiselect={true}
                                selectedOptions={selectedTools}
                                value={selectedToolLabels}
                                placeholder="Select agent tools"
                                onOptionSelect={(_event, data) => handleToolChange(data.selectedOptions)}
                            >
                                {toolOptions.map((tool) => (
                                    <Option key={tool.value} value={tool.value}>
                                        {tool.label}
                                    </Option>
                                ))}
                            </Dropdown>
                        </Field>

                        {enableFunctionCalling && (
                            <Field
                                label={
                                    <AgentFieldLabel
                                        label="Function Tools"
                                        tooltip="Define custom functions this agent can call, including parameter shapes and strict validation."
                                        fieldLabelClassName={styles.fieldLabel}
                                        fieldNameClassName={styles.fieldName}
                                        tooltipIconClassName={styles.tooltipIcon}
                                        tooltipContentClassName={styles.tooltipContent}
                                    />
                                }
                            >
                                <FunctionToolsFieldArray
                                    control={control}
                                    name="functionTools"
                                    watch={watch}
                                    setValue={setValue}
                                />
                            </Field>
                        )}

                        {enableMcpServer && (
                            <Field
                                label={
                                    <AgentFieldLabel
                                        label="MCP Servers"
                                        tooltip="Select MCP servers to attach to this agent. MCP servers extend the agent with external tools."
                                        fieldLabelClassName={styles.fieldLabel}
                                        fieldNameClassName={styles.fieldName}
                                        tooltipIconClassName={styles.tooltipIcon}
                                        tooltipContentClassName={styles.tooltipContent}
                                    />
                                }
                            >
                                {isMcpServersLoading ? (
                                    <Spinner size="tiny" label="Loading MCP servers..." labelPosition="after" />
                                ) : (
                                    <Dropdown
                                        multiselect={true}
                                        selectedOptions={watch('selectedMcpServerIds') ?? []}
                                        value={
                                            availableMcpServers
                                                .filter((s) => (watch('selectedMcpServerIds') ?? []).includes(s.id))
                                                .map((s) => s.name)
                                                .join(', ') || ''
                                        }
                                        placeholder={
                                            availableMcpServers.length === 0
                                                ? 'No MCP servers available'
                                                : 'Select MCP servers'
                                        }
                                        disabled={availableMcpServers.length === 0}
                                        onOptionSelect={(_event, data) =>
                                            setValue('selectedMcpServerIds', data.selectedOptions, {
                                                shouldDirty: true,
                                            })
                                        }
                                    >
                                        {availableMcpServers.map((server) => (
                                            <Option key={server.id} value={server.id} text={server.name}>
                                                <span className={styles.mcpOptionRow}>
                                                    <span>{server.name}</span>
                                                    {server.description && (
                                                        <Tooltip
                                                            content={server.description}
                                                            relationship="description"
                                                            positioning="after"
                                                            withArrow
                                                        >
                                                            <Info16Regular className={styles.mcpOptionInfoIcon} />
                                                        </Tooltip>
                                                    )}
                                                </span>
                                            </Option>
                                        ))}
                                    </Dropdown>
                                )}
                            </Field>
                        )}

                        <Field
                            label={
                                <AgentFieldLabel
                                    label="Allow File Search in Conversations"
                                    tooltip="When enabled, users can upload files for File Search within individual conversations with this agent."
                                    fieldLabelClassName={styles.fieldLabel}
                                    fieldNameClassName={styles.fieldName}
                                    tooltipIconClassName={styles.tooltipIcon}
                                    tooltipContentClassName={styles.tooltipContent}
                                />
                            }
                        >
                            <Switch
                                checked={watch('allowFileSearchAtConversationLevel')}
                                onChange={(_event, data) =>
                                    setValue('allowFileSearchAtConversationLevel', data.checked, { shouldDirty: true })
                                }
                                label={watch('allowFileSearchAtConversationLevel') ? 'Enabled' : 'Disabled'}
                            />
                        </Field>

                        <Field
                            label={
                                <AgentFieldLabel
                                    label="Allow Code Interpreter in Conversations"
                                    tooltip="When enabled, users can upload files for Code Interpreter within individual conversations with this agent."
                                    fieldLabelClassName={styles.fieldLabel}
                                    fieldNameClassName={styles.fieldName}
                                    tooltipIconClassName={styles.tooltipIcon}
                                    tooltipContentClassName={styles.tooltipContent}
                                />
                            }
                        >
                            <Switch
                                checked={watch('allowCodeInterpreterAtConversationLevel')}
                                onChange={(_event, data) =>
                                    setValue('allowCodeInterpreterAtConversationLevel', data.checked, {
                                        shouldDirty: true,
                                    })
                                }
                                label={watch('allowCodeInterpreterAtConversationLevel') ? 'Enabled' : 'Disabled'}
                            />
                        </Field>

                        {enableFileSearch && (
                            <div className={styles.section}>
                                <div className={styles.fieldLabel}>
                                    <Text className={styles.fieldName}>
                                        File Search Files (
                                        {fileSearchFilesToUpload.length + visibleUploadedFileSearchDocuments.length})
                                    </Text>
                                    <Tooltip
                                        relationship="label"
                                        positioning="after"
                                        content={
                                            <div className={styles.tooltipContent}>
                                                Manage files used by the File Search tool.
                                            </div>
                                        }
                                    >
                                        <Info24Regular className={styles.tooltipIcon} />
                                    </Tooltip>
                                </div>
                                <Text className={styles.textHint}>
                                    Max files: {maxFileCount}. Max size per file: {maxFileSizeMb}MB.
                                </Text>
                                <Text className={styles.textHint}>
                                    Accepted: {fileSearchAcceptedExtensions.join(', ')}
                                </Text>

                                <input
                                    ref={fileSearchInputRef}
                                    className={styles.hiddenFileInput}
                                    type="file"
                                    multiple
                                    accept={fileSearchAcceptedExtensions.join(',')}
                                    onChange={(event) => {
                                        void handleUploadFiles(event, {
                                            currentFiles: fileSearchFilesToUpload,
                                            setCurrentFiles: setFileSearchFilesToUpload,
                                            acceptedExtensions: fileSearchAcceptedExtensions,
                                        });
                                    }}
                                />

                                <div className={styles.uploadRow}>
                                    <Button
                                        className={styles.actionButton}
                                        type="button"
                                        onClick={handleDeleteAllFiles}
                                        disabled={totalUploadedFilesCount === 0}
                                    >
                                        Delete All Files
                                    </Button>
                                </div>

                                <div className={styles.section}>
                                    <button
                                        type="button"
                                        className={styles.collapsibleHeader}
                                        onClick={() => setIsFileSearchPendingFilesExpanded((previous) => !previous)}
                                    >
                                        <div className={styles.collapsibleHeaderLeft}>
                                            {isFileSearchPendingFilesExpanded ? (
                                                <ChevronDownRegular className={styles.collapseIcon} />
                                            ) : (
                                                <ChevronRightRegular className={styles.collapseIcon} />
                                            )}
                                            <Text className={styles.fieldName}>
                                                New File Search Files ({fileSearchFilesToUpload.length})
                                            </Text>
                                        </div>
                                    </button>
                                    {isFileSearchPendingFilesExpanded && (
                                        <>
                                            <div className={styles.uploadRow}>
                                                <Button
                                                    className={styles.actionButton}
                                                    type="button"
                                                    onClick={() => handleOpenFilePicker(fileSearchInputRef)}
                                                >
                                                    Upload Files
                                                </Button>
                                                <Button
                                                    className={styles.actionButton}
                                                    type="button"
                                                    onClick={() =>
                                                        handleDeleteSelectedFilesToUpload(
                                                            selectedFileSearchFilesToUpload,
                                                            setFileSearchFilesToUpload,
                                                            setSelectedFileSearchFilesToUpload,
                                                        )
                                                    }
                                                    disabled={selectedFileSearchFilesToUpload.size === 0}
                                                >
                                                    Delete Selected Files
                                                </Button>
                                            </div>
                                            <div className={styles.fileTable}>
                                                <div className={styles.fileTableHeader}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            fileSearchFilesToUpload.length > 0 &&
                                                            selectedFileSearchFilesToUpload.size ===
                                                                fileSearchFilesToUpload.length
                                                        }
                                                        onChange={() =>
                                                            handleToggleSelectAll(
                                                                fileSearchFilesToUpload.map(
                                                                    (file) => `${file.name}-${file.size}`,
                                                                ),
                                                                selectedFileSearchFilesToUpload,
                                                                setSelectedFileSearchFilesToUpload,
                                                            )
                                                        }
                                                    />
                                                    <Text className={styles.fileTableHeaderText}>File</Text>
                                                    <Text className={styles.fileTableHeaderText}>Uploaded Date</Text>
                                                </div>
                                                {fileSearchFilesToUpload.map((file) => (
                                                    <div
                                                        key={`${file.name}-${file.size}`}
                                                        className={styles.fileTableRow}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedFileSearchFilesToUpload.has(
                                                                `${file.name}-${file.size}`,
                                                            )}
                                                            onChange={() =>
                                                                toggleSelection(
                                                                    `${file.name}-${file.size}`,
                                                                    setSelectedFileSearchFilesToUpload,
                                                                )
                                                            }
                                                        />
                                                        <Text className={styles.fileNameText}>{file.name}</Text>
                                                        <Text className={styles.fileDateText}>Pending upload</Text>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className={styles.section}>
                                    <button
                                        type="button"
                                        className={styles.collapsibleHeader}
                                        onClick={() => setIsFileSearchFilesExpanded((previous) => !previous)}
                                    >
                                        <div className={styles.collapsibleHeaderLeft}>
                                            {isFileSearchFilesExpanded ? (
                                                <ChevronDownRegular className={styles.collapseIcon} />
                                            ) : (
                                                <ChevronRightRegular className={styles.collapseIcon} />
                                            )}
                                            <Text className={styles.fieldName}>
                                                Existing File Search Files ({visibleUploadedFileSearchDocuments.length})
                                            </Text>
                                        </div>
                                    </button>
                                    {isFileSearchFilesExpanded && (
                                        <>
                                            <div className={styles.uploadRowRight}>
                                                <Button
                                                    className={styles.actionButton}
                                                    type="button"
                                                    onClick={handleDeleteSelectedUploadedFileSearchDocuments}
                                                    disabled={selectedUploadedFileSearchDocuments.size === 0}
                                                >
                                                    Delete Selected Files
                                                </Button>
                                            </div>
                                            <div className={styles.fileTable}>
                                                <div className={styles.fileTableHeader}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            visibleUploadedFileSearchDocuments.length > 0 &&
                                                            selectedUploadedFileSearchDocuments.size ===
                                                                visibleUploadedFileSearchDocuments.length
                                                        }
                                                        onChange={() =>
                                                            handleToggleSelectAll(
                                                                visibleUploadedFileSearchDocuments.map(
                                                                    (file) => file.fileName,
                                                                ),
                                                                selectedUploadedFileSearchDocuments,
                                                                setSelectedUploadedFileSearchDocuments,
                                                            )
                                                        }
                                                    />
                                                    <Text className={styles.fileTableHeaderText}>File</Text>
                                                    <Text className={styles.fileTableHeaderText}>Uploaded Date</Text>
                                                </div>
                                                {visibleUploadedFileSearchDocuments.map((file) => (
                                                    <div
                                                        key={`filesearch-${file.documentId}`}
                                                        className={styles.fileTableRow}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedUploadedFileSearchDocuments.has(
                                                                file.fileName,
                                                            )}
                                                            onChange={() =>
                                                                toggleSelection(
                                                                    file.fileName,
                                                                    setSelectedUploadedFileSearchDocuments,
                                                                )
                                                            }
                                                        />
                                                        <Text className={styles.fileNameText}>{file.fileName}</Text>
                                                        <Text className={styles.fileDateText}>
                                                            {formatUploadedAt(file.uploadedAt)}
                                                        </Text>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {enableCodeInterpreter && (
                            <div className={styles.section}>
                                <div className={styles.fieldLabel}>
                                    <Text className={styles.fieldName}>
                                        Code Interpreter Files (
                                        {codeInterpreterFilesToUpload.length +
                                            visibleUploadedCodeInterpreterDocuments.length}
                                        )
                                    </Text>
                                    <Tooltip
                                        relationship="label"
                                        positioning="after"
                                        content={
                                            <div className={styles.tooltipContent}>
                                                Manage files used by the Code Interpreter tool.
                                            </div>
                                        }
                                    >
                                        <Info24Regular className={styles.tooltipIcon} />
                                    </Tooltip>
                                </div>
                                <Text className={styles.textHint}>
                                    Max files: {maxFileCount}. Max size per file: {maxFileSizeMb}MB.
                                </Text>
                                <Text className={styles.textHint}>
                                    Accepted: {codeInterpreterAcceptedExtensions.join(', ')}
                                </Text>

                                <input
                                    ref={codeInterpreterInputRef}
                                    className={styles.hiddenFileInput}
                                    type="file"
                                    multiple
                                    accept={codeInterpreterAcceptedExtensions.join(',')}
                                    onChange={(event) => {
                                        void handleUploadFiles(event, {
                                            currentFiles: codeInterpreterFilesToUpload,
                                            setCurrentFiles: setCodeInterpreterFilesToUpload,
                                            acceptedExtensions: codeInterpreterAcceptedExtensions,
                                        });
                                    }}
                                />

                                <div className={styles.uploadRow}>
                                    <Button
                                        className={styles.actionButton}
                                        type="button"
                                        onClick={handleDeleteAllFiles}
                                        disabled={totalUploadedFilesCount === 0}
                                    >
                                        Delete All Files
                                    </Button>
                                </div>

                                <div className={styles.section}>
                                    <button
                                        type="button"
                                        className={styles.collapsibleHeader}
                                        onClick={() =>
                                            setIsCodeInterpreterPendingFilesExpanded((previous) => !previous)
                                        }
                                    >
                                        <div className={styles.collapsibleHeaderLeft}>
                                            {isCodeInterpreterPendingFilesExpanded ? (
                                                <ChevronDownRegular className={styles.collapseIcon} />
                                            ) : (
                                                <ChevronRightRegular className={styles.collapseIcon} />
                                            )}
                                            <Text className={styles.fieldName}>
                                                New Code Interpreter Files ({codeInterpreterFilesToUpload.length})
                                            </Text>
                                        </div>
                                    </button>
                                    {isCodeInterpreterPendingFilesExpanded && (
                                        <>
                                            <div className={styles.uploadRow}>
                                                <Button
                                                    className={styles.actionButton}
                                                    type="button"
                                                    onClick={() => handleOpenFilePicker(codeInterpreterInputRef)}
                                                >
                                                    Upload Files
                                                </Button>
                                                <Button
                                                    className={styles.actionButton}
                                                    type="button"
                                                    onClick={() =>
                                                        handleDeleteSelectedFilesToUpload(
                                                            selectedCodeInterpreterFilesToUpload,
                                                            setCodeInterpreterFilesToUpload,
                                                            setSelectedCodeInterpreterFilesToUpload,
                                                        )
                                                    }
                                                    disabled={selectedCodeInterpreterFilesToUpload.size === 0}
                                                >
                                                    Delete Selected Files
                                                </Button>
                                            </div>
                                            <div className={styles.fileTable}>
                                                <div className={styles.fileTableHeader}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            codeInterpreterFilesToUpload.length > 0 &&
                                                            selectedCodeInterpreterFilesToUpload.size ===
                                                                codeInterpreterFilesToUpload.length
                                                        }
                                                        onChange={() =>
                                                            handleToggleSelectAll(
                                                                codeInterpreterFilesToUpload.map(
                                                                    (file) => `${file.name}-${file.size}`,
                                                                ),
                                                                selectedCodeInterpreterFilesToUpload,
                                                                setSelectedCodeInterpreterFilesToUpload,
                                                            )
                                                        }
                                                    />
                                                    <Text className={styles.fileTableHeaderText}>File</Text>
                                                    <Text className={styles.fileTableHeaderText}>Uploaded Date</Text>
                                                </div>
                                                {codeInterpreterFilesToUpload.map((file) => (
                                                    <div
                                                        key={`${file.name}-${file.size}`}
                                                        className={styles.fileTableRow}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedCodeInterpreterFilesToUpload.has(
                                                                `${file.name}-${file.size}`,
                                                            )}
                                                            onChange={() =>
                                                                toggleSelection(
                                                                    `${file.name}-${file.size}`,
                                                                    setSelectedCodeInterpreterFilesToUpload,
                                                                )
                                                            }
                                                        />
                                                        <Text className={styles.fileNameText}>{file.name}</Text>
                                                        <Text className={styles.fileDateText}>Pending upload</Text>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className={styles.section}>
                                    <button
                                        type="button"
                                        className={styles.collapsibleHeader}
                                        onClick={() => setIsCodeInterpreterFilesExpanded((previous) => !previous)}
                                    >
                                        <div className={styles.collapsibleHeaderLeft}>
                                            {isCodeInterpreterFilesExpanded ? (
                                                <ChevronDownRegular className={styles.collapseIcon} />
                                            ) : (
                                                <ChevronRightRegular className={styles.collapseIcon} />
                                            )}
                                            <Text className={styles.fieldName}>
                                                Existing Code Interpreter Files (
                                                {visibleUploadedCodeInterpreterDocuments.length})
                                            </Text>
                                        </div>
                                    </button>
                                    {isCodeInterpreterFilesExpanded && (
                                        <>
                                            <div className={styles.uploadRowRight}>
                                                <Button
                                                    className={styles.actionButton}
                                                    type="button"
                                                    onClick={handleDeleteSelectedUploadedCodeInterpreterDocuments}
                                                    disabled={selectedUploadedCodeInterpreterDocuments.size === 0}
                                                >
                                                    Delete Selected Files
                                                </Button>
                                            </div>
                                            <div className={styles.fileTable}>
                                                <div className={styles.fileTableHeader}>
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            visibleUploadedCodeInterpreterDocuments.length > 0 &&
                                                            selectedUploadedCodeInterpreterDocuments.size ===
                                                                visibleUploadedCodeInterpreterDocuments.length
                                                        }
                                                        onChange={() =>
                                                            handleToggleSelectAll(
                                                                visibleUploadedCodeInterpreterDocuments.map(
                                                                    (file) => file.fileName,
                                                                ),
                                                                selectedUploadedCodeInterpreterDocuments,
                                                                setSelectedUploadedCodeInterpreterDocuments,
                                                            )
                                                        }
                                                    />
                                                    <Text className={styles.fileTableHeaderText}>File</Text>
                                                    <Text className={styles.fileTableHeaderText}>Uploaded Date</Text>
                                                </div>
                                                {visibleUploadedCodeInterpreterDocuments.map((file) => (
                                                    <div
                                                        key={`codeinterpreter-${file.documentId}`}
                                                        className={styles.fileTableRow}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedUploadedCodeInterpreterDocuments.has(
                                                                file.fileName,
                                                            )}
                                                            onChange={() =>
                                                                toggleSelection(
                                                                    file.fileName,
                                                                    setSelectedUploadedCodeInterpreterDocuments,
                                                                )
                                                            }
                                                        />
                                                        <Text className={styles.fileNameText}>{file.fileName}</Text>
                                                        <Text className={styles.fileDateText}>
                                                            {formatUploadedAt(file.uploadedAt)}
                                                        </Text>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className={styles.metadataSeparator}>
                            <div className={styles.metadataSeparatorLine} />
                            <Text className={styles.metadataSeparatorText}>Agent Metadata</Text>
                            <div className={styles.metadataSeparatorLine} />
                        </div>

                        <Field
                            label={
                                <AgentFieldLabel
                                    label="Upload Card Image"
                                    tooltip="Upload the image you want to display as a thumbnail for this agent card."
                                    fieldLabelClassName={styles.fieldLabel}
                                    fieldNameClassName={styles.fieldName}
                                    tooltipIconClassName={styles.tooltipIcon}
                                    tooltipContentClassName={styles.tooltipContent}
                                />
                            }
                        >
                            <input
                                ref={imageInputRef}
                                className={styles.hiddenFileInput}
                                type="file"
                                accept="image/*"
                                onChange={handleImageFileChange}
                            />
                            <div className={styles.uploadImageRow}>
                                <Button
                                    className={styles.actionButton}
                                    type="button"
                                    onClick={() => imageInputRef.current?.click()}
                                >
                                    Upload Image
                                </Button>
                                {(selectedImageFile || watch('agentIconUrl')?.startsWith('https://')) && (
                                    <img
                                        src={selectedImageFile ? URL.createObjectURL(selectedImageFile) : watch('agentIconUrl')}
                                        alt="Agent icon preview"
                                        className={styles.iconPreview}
                                    />
                                )}
                                <Text className={styles.textHint}>
                                    {selectedImageFile?.name ?? (watch('agentIconUrl')?.startsWith('https://') ? 'Current icon' : 'No image selected')}
                                </Text>
                            </div>
                        </Field>

                        <Field
                            label={
                                <div className={styles.fieldLabel}>
                                    <Text className={styles.fieldName}>Card Short Description</Text>
                                    <Tooltip
                                        relationship="label"
                                        positioning="after"
                                        content={
                                            <div className={styles.tooltipContent}>
                                                Provide a short summary to display on the agent card.
                                            </div>
                                        }
                                    >
                                        <Info24Regular className={styles.tooltipIcon} />
                                    </Tooltip>
                                </div>
                            }
                            validationMessage={errors.cardShortDescription?.message}
                            validationState={errors.cardShortDescription ? 'error' : 'none'}
                        >
                            <Controller
                                name="cardShortDescription"
                                control={control}
                                rules={{
                                    maxLength: {
                                        value: CARD_SHORT_DESCRIPTION_MAX_LENGTH,
                                        message: `Card short description cannot exceed ${CARD_SHORT_DESCRIPTION_MAX_LENGTH} characters.`,
                                    },
                                }}
                                render={({ field }) => (
                                    <Input
                                        value={field.value ?? ''}
                                        onChange={(event) => field.onChange(event.target.value)}
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        maxLength={CARD_SHORT_DESCRIPTION_MAX_LENGTH}
                                        placeholder="Short card description"
                                    />
                                )}
                            />
                            <Text className={styles.characterCount}>
                                {cardShortDescriptionValue.length}/{CARD_SHORT_DESCRIPTION_MAX_LENGTH} characters
                            </Text>
                        </Field>

                        <Field
                            label={
                                <div className={styles.fieldLabel}>
                                    <Text className={styles.fieldName}>Disclaimer</Text>
                                    <Tooltip
                                        relationship="label"
                                        positioning="after"
                                        content={
                                            <div className={styles.tooltipContent}>
                                                Optional disclaimer text shown alongside the agent details.
                                            </div>
                                        }
                                    >
                                        <Info24Regular className={styles.tooltipIcon} />
                                    </Tooltip>
                                </div>
                            }
                            validationMessage={errors.disclaimer?.message}
                            validationState={errors.disclaimer ? 'error' : 'none'}
                        >
                            <Textarea
                                {...register('disclaimer', {
                                    maxLength: {
                                        value: DISCLAIMER_MAX_LENGTH,
                                        message: `Disclaimer cannot exceed ${DISCLAIMER_MAX_LENGTH} characters.`,
                                    },
                                })}
                                maxLength={DISCLAIMER_MAX_LENGTH}
                                placeholder="Optional disclaimer"
                            />
                            <Text className={styles.characterCount}>
                                {disclaimerValue.length}/{DISCLAIMER_MAX_LENGTH} characters
                            </Text>
                        </Field>

                        <Field
                            label={
                                <div className={styles.fieldLabel}>
                                    <Text className={styles.fieldName}>Agent Knowledge Base</Text>
                                    <Tooltip
                                        relationship="label"
                                        positioning="after"
                                        content={
                                            <div className={styles.tooltipContent}>
                                                Provide a knowledge base summary for this agent.
                                            </div>
                                        }
                                    >
                                        <Info24Regular className={styles.tooltipIcon} />
                                    </Tooltip>
                                </div>
                            }
                        >
                            <Textarea
                                {...register('knowledgeBaseContent', {
                                    maxLength: {
                                        value: KNOWLEDGE_BASE_MAX_LENGTH,
                                        message: `Knowledge base content cannot exceed ${KNOWLEDGE_BASE_MAX_LENGTH} characters.`,
                                    },
                                })}
                                maxLength={KNOWLEDGE_BASE_MAX_LENGTH}
                                placeholder="Knowledge base summary"
                            />
                            <Text className={styles.characterCount}>
                                {knowledgeBaseValue.length}/{KNOWLEDGE_BASE_MAX_LENGTH} characters
                            </Text>
                        </Field>

                        <div className={styles.section}>
                            <div className={styles.fieldLabel}>
                                <Text className={styles.fieldName}>Prompt Library</Text>
                                <Tooltip
                                    relationship="label"
                                    positioning="after"
                                    content={
                                        <div className={styles.tooltipContent}>
                                            Add reusable prompt entries for this agent.
                                        </div>
                                    }
                                >
                                    <Info24Regular className={styles.tooltipIcon} />
                                </Tooltip>
                            </div>

                            {promptFields.map((field, index) => (
                                <div key={field.id} className={styles.subItemCard}>
                                    <Controller
                                        name={`promptLibrary.${index}.title` as const}
                                        control={control}
                                        rules={{
                                            maxLength: {
                                                value: SHORT_ENTRY_MAX_LENGTH,
                                                message: `Prompt title cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                            },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                value={field.value ?? ''}
                                                onChange={(event) => field.onChange(event.target.value)}
                                                onBlur={field.onBlur}
                                                name={field.name}
                                                maxLength={SHORT_ENTRY_MAX_LENGTH}
                                                placeholder="Title"
                                            />
                                        )}
                                    />
                                    <Textarea
                                        {...register(`promptLibrary.${index}.prompt` as const, {
                                            maxLength: {
                                                value: PROMPT_DESCRIPTION_MAX_LENGTH,
                                                message: `Prompt cannot exceed ${PROMPT_DESCRIPTION_MAX_LENGTH} characters.`,
                                            },
                                        })}
                                        maxLength={PROMPT_DESCRIPTION_MAX_LENGTH}
                                        placeholder="Prompt"
                                    />

                                    <Button
                                        type="button"
                                        className={styles.compactButton}
                                        disabled={promptFields.length === 1}
                                        onClick={() => removePrompt(index)}
                                    >
                                        Remove Prompt
                                    </Button>
                                </div>
                            ))}
                            <div className={styles.addButtonRow}>
                                <Button
                                    className={styles.compactButton}
                                    type="button"
                                    disabled={promptFields.length >= PROMPT_MAX_ITEMS}
                                    onClick={() => appendPrompt({ title: '', prompt: '' })}
                                >
                                    Add Prompt
                                </Button>
                            </div>
                        </div>

                        <div className={styles.section}>
                            <div className={styles.fieldLabel}>
                                <Text className={styles.fieldName}>FAQs</Text>
                                <Tooltip
                                    relationship="label"
                                    positioning="after"
                                    content={
                                        <div className={styles.tooltipContent}>
                                            Add frequently asked questions and answers for this agent.
                                        </div>
                                    }
                                >
                                    <Info24Regular className={styles.tooltipIcon} />
                                </Tooltip>
                            </div>

                            {faqFields.map((field, index) => (
                                <div key={field.id} className={styles.subItemCard}>
                                    <Controller
                                        name={`faqs.${index}.question` as const}
                                        control={control}
                                        rules={{
                                            maxLength: {
                                                value: SHORT_ENTRY_MAX_LENGTH,
                                                message: `Question cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                            },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                value={field.value ?? ''}
                                                onChange={(event) => field.onChange(event.target.value)}
                                                onBlur={field.onBlur}
                                                name={field.name}
                                                maxLength={SHORT_ENTRY_MAX_LENGTH}
                                                placeholder="Question"
                                            />
                                        )}
                                    />
                                    <Textarea
                                        {...register(`faqs.${index}.answer` as const, {
                                            maxLength: {
                                                value: SHORT_ENTRY_MAX_LENGTH,
                                                message: `Answer cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                            },
                                        })}
                                        maxLength={SHORT_ENTRY_MAX_LENGTH}
                                        placeholder="Answer"
                                    />

                                    <Button
                                        type="button"
                                        className={styles.compactButton}
                                        disabled={faqFields.length === 1}
                                        onClick={() => removeFaq(index)}
                                    >
                                        Remove FAQ
                                    </Button>
                                </div>
                            ))}
                            <div className={styles.addButtonRow}>
                                <Button
                                    className={styles.compactButton}
                                    type="button"
                                    disabled={faqFields.length >= FAQ_MAX_ITEMS}
                                    onClick={() => appendFaq({ question: '', answer: '' })}
                                >
                                    Add FAQ
                                </Button>
                            </div>
                        </div>

                        <div className={styles.section}>
                            <div className={styles.fieldLabel}>
                                <Text className={styles.fieldName}>Agent Owner Info</Text>
                                <Tooltip
                                    relationship="label"
                                    positioning="after"
                                    content={
                                        <div className={styles.tooltipContent}>
                                            Add the email addresses for the owners of this agent.
                                        </div>
                                    }
                                >
                                    <Info24Regular className={styles.tooltipIcon} />
                                </Tooltip>
                            </div>

                            {ownerFields.map((field, index) => (
                                <div key={field.id} className={styles.subItemCard}>
                                    <Field
                                        validationMessage={errors.ownerInfo?.[index]?.userPrincipalName?.message}
                                        validationState={
                                            errors.ownerInfo?.[index]?.userPrincipalName ? 'error' : 'none'
                                        }
                                    >
                                        <Controller
                                            name={`ownerInfo.${index}.userPrincipalName` as const}
                                            control={control}
                                            rules={{
                                                validate: (value) => validateCompanyEmail(value),
                                            }}
                                            render={({ field }) => (
                                                <Input
                                                    value={field.value ?? ''}
                                                    onChange={(event) => field.onChange(event.target.value)}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    placeholder="owner@roberthalf.com"
                                                />
                                            )}
                                        />
                                    </Field>
                                    <Button
                                        type="button"
                                        className={styles.compactButton}
                                        disabled={ownerFields.length === 1}
                                        onClick={() => removeOwner(index)}
                                    >
                                        Remove Owner
                                    </Button>
                                </div>
                            ))}
                            <div className={styles.addButtonRow}>
                                <Button
                                    className={styles.compactButton}
                                    type="button"
                                    onClick={() =>
                                        appendOwner({
                                            userId: '',
                                            givenName: '',
                                            surName: '',
                                            userPrincipalName: '',
                                            mail: '',
                                            photoUrl: '',
                                        })
                                    }
                                >
                                    Add Owner
                                </Button>
                            </div>
                        </div>

                        <div className={styles.section}>
                            <div className={styles.fieldLabel}>
                                <Text className={styles.fieldName}>Agent Support Address</Text>
                                <Tooltip
                                    relationship="label"
                                    positioning="after"
                                    content={
                                        <div className={styles.tooltipContent}>
                                            Provide a support contact address for this agent.
                                        </div>
                                    }
                                >
                                    <Info24Regular className={styles.tooltipIcon} />
                                </Tooltip>
                            </div>

                            {supportAddressFields.map((field, index) => (
                                <div key={field.id} className={styles.subItemCard}>
                                    <Field
                                        validationMessage={errors.supportAddress?.[index]?.email?.message}
                                        validationState={errors.supportAddress?.[index]?.email ? 'error' : 'none'}
                                    >
                                        <Controller
                                            name={`supportAddress.${index}.email` as const}
                                            control={control}
                                            rules={{
                                                validate: (value) => validateCompanyEmail(value),
                                            }}
                                            render={({ field }) => (
                                                <Input
                                                    value={field.value ?? ''}
                                                    onChange={(event) => field.onChange(event.target.value)}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    placeholder="support@roberthalf.com"
                                                />
                                            )}
                                        />
                                    </Field>
                                    <Button
                                        type="button"
                                        className={styles.compactButton}
                                        onClick={() => removeSupportAddress(index)}
                                    >
                                        Remove Support Address
                                    </Button>
                                </div>
                            ))}
                            {supportAddressFields.length === 0 && (
                                <div className={styles.addButtonRow}>
                                    <Button
                                        className={styles.compactButton}
                                        type="button"
                                        onClick={() => appendSupportAddress({ email: '' })}
                                    >
                                        Add Support Address
                                    </Button>
                                </div>
                            )}
                        </div>

                        <div className={styles.section}>
                            <div className={styles.fieldLabel}>
                                <Text className={styles.fieldName}>Agent Capabilities</Text>
                                <Tooltip
                                    relationship="label"
                                    positioning="after"
                                    content={
                                        <div className={styles.tooltipContent}>
                                            List the key capabilities this agent provides.
                                        </div>
                                    }
                                >
                                    <Info24Regular className={styles.tooltipIcon} />
                                </Tooltip>
                            </div>

                            {capabilityFields.map((field, index) => (
                                <div key={field.id} className={styles.subItemCard}>
                                    <div className={styles.inlineActionRow}>
                                        <Controller
                                            name={`capabilities.${index}.value` as const}
                                            control={control}
                                            rules={{
                                                maxLength: {
                                                    value: CAPABILITY_MAX_LENGTH,
                                                    message: `Capability cannot exceed ${CAPABILITY_MAX_LENGTH} characters.`,
                                                },
                                            }}
                                            render={({ field }) => (
                                                <Input
                                                    value={field.value ?? ''}
                                                    onChange={(event) => field.onChange(event.target.value)}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    maxLength={CAPABILITY_MAX_LENGTH}
                                                    placeholder="Capability"
                                                />
                                            )}
                                        />

                                        <Button
                                            type="button"
                                            className={styles.compactButton}
                                            disabled={capabilityFields.length === 1}
                                            onClick={() => removeCapability(index)}
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            <div className={styles.addButtonRow}>
                                <Button
                                    className={styles.compactButton}
                                    type="button"
                                    disabled={capabilityFields.length >= CAPABILITIES_MAX_ITEMS}
                                    onClick={() => appendCapability({ value: '' })}
                                >
                                    Add Capability
                                </Button>
                            </div>
                        </div>

                        <div className={styles.section}>
                            <div className={styles.fieldLabel}>
                                <Text className={styles.fieldName}>Related Links</Text>
                                <Tooltip
                                    relationship="label"
                                    positioning="after"
                                    content={
                                        <div className={styles.tooltipContent}>
                                            Add related reference links for this agent.
                                        </div>
                                    }
                                >
                                    <Info24Regular className={styles.tooltipIcon} />
                                </Tooltip>
                            </div>

                            {relatedLinkFields.map((field, index) => (
                                <div key={field.id} className={styles.subItemCard}>
                                    <Controller
                                        name={`relatedLinks.${index}.title` as const}
                                        control={control}
                                        rules={{
                                            maxLength: {
                                                value: SHORT_ENTRY_MAX_LENGTH,
                                                message: `Link title cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                            },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                value={field.value ?? ''}
                                                onChange={(event) => field.onChange(event.target.value)}
                                                onBlur={field.onBlur}
                                                name={field.name}
                                                maxLength={SHORT_ENTRY_MAX_LENGTH}
                                                placeholder="Link title"
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`relatedLinks.${index}.link` as const}
                                        control={control}
                                        rules={{
                                            maxLength: {
                                                value: SHORT_ENTRY_MAX_LENGTH,
                                                message: `Link cannot exceed ${SHORT_ENTRY_MAX_LENGTH} characters.`,
                                            },
                                        }}
                                        render={({ field }) => (
                                            <Input
                                                value={field.value ?? ''}
                                                onChange={(event) => field.onChange(event.target.value)}
                                                onBlur={field.onBlur}
                                                name={field.name}
                                                maxLength={SHORT_ENTRY_MAX_LENGTH}
                                                placeholder="https://example.com"
                                            />
                                        )}
                                    />

                                    <Button
                                        type="button"
                                        className={styles.compactButton}
                                        disabled={relatedLinkFields.length === 1}
                                        onClick={() => removeRelatedLink(index)}
                                    >
                                        Remove Link
                                    </Button>
                                </div>
                            ))}
                            <div className={styles.addButtonRow}>
                                <Button
                                    className={styles.compactButton}
                                    type="button"
                                    disabled={relatedLinkFields.length >= RELATED_LINK_MAX_ITEMS}
                                    onClick={() => appendRelatedLink({ title: '', link: '' })}
                                >
                                    Add Link
                                </Button>
                            </div>
                        </div>

                        <div className={styles.buttonRow}>
                            <Button
                                className={styles.actionButton}
                                appearance="secondary"
                                type="button"
                                onClick={vm.onCancel}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                className={styles.actionButton}
                                appearance="primary"
                                type="submit"
                                disabled={isSubmitting || !vm.agentId}
                            >
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </>
                )}
            </form>
        </div>
    );
};

export default EditAgentPage;
